import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter, fromEvent, interval, merge } from 'rxjs';

export const VERSION_POLL_MS = 5 * 60_000;
const RELOAD_KEY = 'chunk-reload-at';

/** A lazy chunk of a build that is no longer deployed. */
export const isChunkLoadError = (e: unknown): boolean =>
    /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(
        String((e as any)?.message ?? e)
    );

/**
 * Tabs left open across an FE deploy: reload once when a lazy chunk is gone, and when version.json changes
 * offer a reload, else reload after the next navigation (the user has already left the page, so no form is lost).
 */
@Injectable({ providedIn: 'root' })
export class StaleBuildService {
    private _router = inject(Router);
    private _snackBar = inject(MatSnackBar);
    private _build: string | null = null;
    private _stale = false;

    constructor() {
        this._router.events.subscribe((e) => {
            if (e instanceof NavigationError && isChunkLoadError(e.error)) this._reloadOnce(e.url);
            if (e instanceof NavigationEnd) {
                if (this._stale) location.reload();
                else sessionStorage.removeItem(RELOAD_KEY);
            }
        });
        this._check();
        merge(interval(VERSION_POLL_MS), fromEvent(document, 'visibilitychange').pipe(filter(() => !document.hidden)))
            .subscribe(() => this._check());
    }

    /** At most one reload a minute, so a chunk that is really missing can't loop. */
    private _reloadOnce(url: string): void {
        const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
        if (Date.now() - last < 60_000) return;
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        location.assign(url);
    }

    private async _check(): Promise<void> {
        if (this._stale) return;
        try {
            const v = await (await fetch('assets/version.json', { cache: 'no-store' })).json();
            const build = `${v.release}|${v.commit}|${v.builtAt}`;
            if (this._build === null) this._build = build;
            else if (build !== this._build) this._markStale();
        } catch {
            // offline or no version.json (local): try again later
        }
    }

    private _markStale(): void {
        this._stale = true;
        this._snackBar
            .open('A new version of Propel is available.', 'Reload')
            .onAction()
            .subscribe(() => location.reload());
    }
}
